import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import qs from 'qs'
import { notification } from 'ant-design-vue'
import { isUat } from '@/utils/tool'

notification.config({
  placement: 'topRight',
  getContainer: () => document.querySelector('micro-app-body') || document.body,
})

// 拦截重复的请求
// 重复的请求是指请求方式、请求路径、请求参数都一致的请求
const pendingMap = new Map()
// 生成每个请求唯一的键
function getPendingKey(config: AxiosRequestConfig) {
  const { url, method, params, data } = config
  return [url, method, JSON.stringify(params), JSON.stringify(data)].join('&')
}
// 存储每个请求的唯一值
function addPendingKey(config: AxiosRequestConfig) {
  const pendingKey = getPendingKey(config)
  config.cancelToken =
    config.cancelToken ||
    new axios.CancelToken((cancel) => {
      if (!pendingMap.has(pendingKey)) {
        pendingMap.set(pendingKey, cancel)
      }
    })
}
// 取出重复请求并且删除
function removePendingKey(config: AxiosRequestConfig) {
  const pendingKey = getPendingKey(config)
  if (pendingMap.has(pendingKey)) {
    const cancelToken = pendingMap.get(pendingKey)
    cancelToken(pendingKey)
    pendingMap.delete(pendingKey)
  }
}

interface IHttpConfig {
  options?: AxiosRequestConfig
  responseHook?: (result: AxiosResponse) => void
  notify?: (message: string) => void
}

// 普通请求
export class HttpRequest {
  // 设置基准路径
  instance: AxiosInstance
  options: AxiosRequestConfig
  notify: (message: string) => void
  constructor(config?: IHttpConfig) {
    this.options = {
      timeout: 20000,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      baseURL: isUat ? '//uat-api.live.bilibili.com' : '//api.live.bilibili.com',
      ...(config?.options ?? undefined),
    }
    this.instance = axios.create({ withCredentials: true, ...this.options })
    this.interceptors(config?.responseHook)
    // 接受一个自定义通知实例，默认使用antd的notification
    this.notify =
      config?.notify ||
      function (message: string) {
        notification.error({
          message: '错误',
          description: message,
        })
      }
  }

  // 设置拦截器
  interceptors(responseHook?: (result: AxiosResponse) => void) {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config) => {
        removePendingKey(config)
        addPendingKey(config)
        return config
      },
      (error) => {
        this.notify(error.message || error.msg)
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.instance.interceptors.response.use(
      (result) => {
        removePendingKey(result.config)
        // 外部hook
        typeof responseHook === 'function' && responseHook(result)

        if (![0, 200].includes(result.data.code)) {
          this.notify(result.data.message || result.data.msg)
          return Promise.reject(result.data)
        }
        return Promise.resolve(result.data)
      },
      (error) => {
        error.config && removePendingKey(error.config)
        !error.__CANCEL__ && this.notify(error.message || error.msg)
        return Promise.reject(error)
      }
    )
  }

  async get<T>(url: string, params?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const result = await this.instance.get<T>(url, { params, ...config })
    return result.data
  }

  async post<T>(url: string, params?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const result = await this.instance.post<T>(url, qs.stringify(params), config)
    return result.data
  }

  async form<T>(url: string, params?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const result = await this.instance.post<T>(url, params, config)
    return result.data
  }

  async json<T>(url: string, params?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const result = await this.instance.post<T>(url, params, { ...config, headers: { 'Content-Type': 'application/json' } })
    return result.data
  }
}
